import { JSX } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Button,
    IconButton,
    Typography,
    styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Nok } from "../../../../types/entities";
import {
    Form,
    ArrayInput,
    SimpleFormIterator,
    TextInput,
    required,
    BooleanInput
} from 'react-admin';
import { useFormContext } from "react-hook-form"

type NokConfigureDialogProps = {
    open: boolean;
    handleClose: () => void,
    activeConfigureNok: Nok,

};

// The system_data_point values that have a configuration block below. Anything outside this
// list (or a null/absent data point) means there is nothing for an admin to fill in.
const DATA_POINT_BLOCKS: string[] = [
    'custom_values',
    'company_name',
    'company_address',
    'custom_value',
    'privacy_policy',
];

// Must stay at module scope. Defining it inside the component calls styled() on every
// render, which produces a new component type each time and remounts the whole form
// subtree — losing input focus mid-keystroke.
const StyledForm: any = styled(Form)({
    width: '100%',
    height: '100%',
});

export const NokConfigureDialog: (props: NokConfigureDialogProps) => JSX.Element = (props: NokConfigureDialogProps): JSX.Element => {
    const configuration: any = props?.activeConfigureNok?.configuration?.[0];
    const { setValue, getValues } = useFormContext();

    // Derived up front so the "no configuration" notice is a genuine else-branch. The previous
    // inline condition read `a && b === undefined || c === null && d !== 'x'`, which parses as
    // `(a && b === undefined) || (c === null && d !== 'x')` — not what was meant, and it could
    // render the notice alongside a configuration block rather than instead of one.
    const systemDataPoint: string = configuration?.system_data_point ?? '';
    const isComplexNok: boolean = props?.activeConfigureNok?.type === 'complex';
    const isCustomerProvided: boolean = configuration?.processing_type === 'customer_provided';
    const hasConfigurableFields: boolean = isComplexNok || DATA_POINT_BLOCKS.includes(systemDataPoint);

    // The dialog remounts each time it opens, so reading this at render is accurate. It drives
    // the wording: the dialog previously always said "Add Nok" and always offered "Remove Nok",
    // regardless of whether the Nok was actually in the selection.
    const isAlreadyAdded: boolean = (getValues('noks') || []).some(
        (nok: Nok): boolean => nok?.operatorId === props?.activeConfigureNok?.operatorId
    );

    function handleFormSubmit(data: any): void {
        const record: Nok = props?.activeConfigureNok;
        if (data !== undefined) {
            record.configuration[0] = data
        }
        const activeNoks: Nok[] = getValues('noks') || [];
        const found: boolean = activeNoks.some(
            (nok: Nok): boolean => nok?.operatorId === record?.operatorId
        );

        // Always hand setValue a new array. Pushing into the array returned by getValues
        // and passing that same reference back means subscribers can compare it against
        // itself and skip the update, leaving the Selected checkbox stale.
        const newNoks: Nok[] = found ? [ ...activeNoks ] : [ ...activeNoks, record ];

        setValue('noks', newNoks, { shouldDirty: true });
        props?.handleClose();
    }

    function removeNok(): void {
        const record: Nok = props?.activeConfigureNok;
        let activeNoks: Nok[] = getValues('noks') || [];
        let newNoks: Nok[] = [];
        for(const nok of activeNoks) {
            if (nok.operatorId !== record.operatorId) {
                newNoks.push(nok);
            }
        }
        setValue('noks', newNoks, { shouldDirty: true });
        props?.handleClose();
    }

    function handleCancelButtonClick(): void {
        props?.handleClose();
    }

    return (
        <Dialog open={props.open}
                fullWidth
                maxWidth="sm"
                onClose={props.handleClose} >
            <DialogTitle sx={ {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pr: 1,
            } }>
                <span>
                    { `${ isAlreadyAdded ? 'Configure' : 'Add & Configure' } Nok - ${props?.activeConfigureNok?.name}` }
                </span>
                <IconButton onClick={ props?.handleClose } aria-label="Close" size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={ {
                    width: '100%',
                    height: '500px'
                } }>
                    <Box sx={ {
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    } }>
                        <Box sx={ { width: '100%', height: '100%' } }>
                            <StyledForm onSubmit={ handleFormSubmit } defaultValues={ configuration }  >
                                <Box sx={ { height: '100%', display: 'flex', flexDirection: 'column' } }>
                                    { !hasConfigurableFields &&
                                        <Box sx={ {
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                        } }>
                                            <Typography variant="body2" color="text.secondary">
                                                { isCustomerProvided
                                                    ? 'This Nok is configured by the customer, so there is nothing to set here.'
                                                    : 'This Nok has no configuration.' }
                                            </Typography>
                                        </Box>
                                    }
                                    { (systemDataPoint === 'custom_values') &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <ArrayInput source="custom_values_value" label="Custom Search Parameters" validate={[required()]}>
                                                <SimpleFormIterator>
                                                    <TextInput source="search_item" />
                                                </SimpleFormIterator>
                                            </ArrayInput>
                                        </Box>
                                    }
                                    { (systemDataPoint === 'company_name') &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <TextInput source="company_name_value" validate={[required()]} label="Company Name" />
                                        </Box>

                                    }
                                    { (systemDataPoint === 'company_address') &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <ArrayInput source="company_address_value" label="Company Addresses" validate={[required()]}>
                                                <SimpleFormIterator >
                                                    <TextInput source="line_one" validate={[required()]} label="Address Line 1" />
                                                    <TextInput source="line_two" label="Address Line 2" />
                                                    <TextInput source="city" label="City" />
                                                    <TextInput source="county" label="County/State" />
                                                    <TextInput source="postcode" validate={[required()]} label="Postcode" />
                                                </SimpleFormIterator>
                                            </ArrayInput>
                                        </Box>

                                    }
                                    { (systemDataPoint === 'custom_value') &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <TextInput source="custom_value_value" label="Custom Value"  validate={[required()]}/>
                                        </Box>

                                    }
                                    { (systemDataPoint === 'privacy_policy') &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <TextInput source="privacy_policy_value" label="Privacy Policy URL"  validate={[required()]}/>
                                        </Box>

                                    }
                                    { isComplexNok &&
                                        <Box sx={ { height: '90%', overflowY: 'auto' } }>
                                            <BooleanInput source='office_hours_only' defaultValue={ false } />
                                            <TextInput source="office_start" label="Office Start Time" validate={[required()]} defaultValue="09:00"/>
                                            <TextInput source="office_end" label="Office End Time" validate={[required()]} defaultValue="17:30"/>
                                            <TextInput source="timezone_string" label="Timezone"  validate={[required()]} defaultValue="UTC"/>
                                        </Box>
                                    }

                                    { /* These stay inside the form: "Add Nok" is the submit
                                         button, so hoisting them into DialogActions (which sits
                                         outside the form element) would break submission. */ }
                                    <Box sx={ {
                                        mt: 'auto',
                                        pt: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    } }>
                                        <Button variant="contained"
                                                type="submit"
                                                color="primary">
                                            <span>{ isAlreadyAdded ? "Save changes" : "Add Nok" }</span>
                                        </Button>
                                        <Box sx={ { display: 'flex', gap: 1 } }>
                                            { isAlreadyAdded &&
                                                <Button variant="contained"
                                                        type="button"
                                                        onClick={ removeNok }
                                                        color="error">
                                                    <span>{ "Remove Nok" }</span>
                                                </Button>
                                            }
                                            <Button variant="contained"
                                                    type="button"
                                                    onClick={ handleCancelButtonClick }
                                                    color="secondary">
                                                <span>{ "Cancel" }</span>
                                            </Button>
                                        </Box>

                                    </Box>
                                </Box>



                            </StyledForm>
                        </Box>

                    </Box>

                </Box>
            </DialogContent>
        </Dialog>
    );
}